import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DemoPlanPopup = ({ isOpen, onClose, demoPlanData }) => {
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300);
  };

  const handlePurchasePlans = () => {
    navigate('/subscription');
    handleClose();
  };

  if (!isOpen || !demoPlanData) return null;

  const {
    activeDays = 7,
    features = [],
    user = {},
    FeaturesAccessData = [],
  } = demoPlanData;

  // Calculate remaining days from FeaturesAccessData expiredDate
  const calculateRemainingDays = () => {
    if (!FeaturesAccessData || FeaturesAccessData.length === 0) {
      return activeDays;
    }

    const expiredDate = new Date(FeaturesAccessData[0]?.expiredDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiredDate.setHours(0, 0, 0, 0);

    const timeDiff = expiredDate - today;
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return Math.max(0, daysDiff);
  };

  const remainingDays = calculateRemainingDays();
  const totalDays = 7; // Demo account is for 7 days
  const progressPercentage = Math.min((remainingDays / totalDays) * 100, 100);

  const getContactLimit = (data, dailyKey, totalKey) => {
    if (!data || data.length === 0) return { daily: 0, total: 0 };
    return {
      daily: data[0]?.[dailyKey] || 0,
      total: data[0]?.[totalKey] || 0,
    };
  };

  const ownerLimit = getContactLimit(
    demoPlanData?.OwnerContact || user?.OwnerContact,
    'OwnerDailyContactLimit',
    'OwnerTotalContactLimit'
  );
  const brokerLimit = getContactLimit(
    demoPlanData?.BrokerContact || user?.BrokerContact,
    'brokerContactLimit',
    'BrokerTotalContactLimit'
  );
  const builderLimit = getContactLimit(
    demoPlanData?.BuilderContact || user?.BuilderContact,
    'builderContactLimit',
    'BuilderTotalContactLimit'
  );
  const requirementLimit = getContactLimit(
    demoPlanData?.RequirementContact || user?.RequirementContact,
    'requirementContactLimit',
    'RequirementTotalContactLimit'
  );

  const featureLabels = {
    OWNER_PROPERTY: { label: 'Owner Properties', icon: '🏠', color: '#FF6B6B' },
    BROKER_PROPERTY: { label: 'Broker Properties', icon: '🏢', color: '#4ECDC4' },
    BUILDER_PROPERTY: { label: 'Builder Properties', icon: '🏗️', color: '#FFD93D' },
  };

  return (
    <>
      <style>{`
        @keyframes popupSlideIn {
          0% {
            opacity: 0;
            transform: scale(0.85) translateY(40px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes floatPattern {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(40px, 40px);
          }
        }

        @keyframes badgeBounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }

        @keyframes progressPulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(80, 54, 145, 0.4);
          }
          50% {
            box-shadow: 0 0 8px 4px rgba(80, 54, 145, 0.2);
          }
        }

        @keyframes featureFadeIn {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes limitCardFadeIn {
          0% {
            opacity: 0;
            transform: translateX(-10px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes actionsFadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        .demo-popup-card {
          animation: popupSlideIn 0.5s ease-out;
        }

        .demo-header-pattern::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 40px 40px;
          animation: floatPattern 20s linear infinite;
        }

        .demo-badge-bounce {
          animation: badgeBounce 2s ease-in-out infinite;
        }

        .progress-bar-animated {
          animation: progressPulse 2s ease-in-out infinite;
        }

        .feature-fade-1 {
          animation: featureFadeIn 0.6s ease-out 0.1s forwards;
          opacity: 0;
        }

        .feature-fade-2 {
          animation: featureFadeIn 0.6s ease-out 0.2s forwards;
          opacity: 0;
        }

        .feature-fade-3 {
          animation: featureFadeIn 0.6s ease-out 0.3s forwards;
          opacity: 0;
        }

        .limit-fade-1 {
          animation: limitCardFadeIn 0.6s ease-out 0.2s forwards;
          opacity: 0;
        }

        .limit-fade-2 {
          animation: limitCardFadeIn 0.6s ease-out 0.3s forwards;
          opacity: 0;
        }

        .limit-fade-3 {
          animation: limitCardFadeIn 0.6s ease-out 0.4s forwards;
          opacity: 0;
        }

        .limit-fade-4 {
          animation: limitCardFadeIn 0.6s ease-out 0.5s forwards;
          opacity: 0;
        }

        .actions-fade {
          animation: actionsFadeIn 0.6s ease-out 0.5s forwards;
          opacity: 0;
        }

        .limit-card-shine::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transition: left 0.5s ease;
        }

        .limit-card-shine:hover::before {
          left: 100%;
        }

        .section-title-line::before {
          content: '';
          width: 4px;
          height: 20px;
          background: linear-gradient(135deg, #503691, #7c5cdb);
          border-radius: 2px;
        }

        /* Scrollbar Styling */
        .demo-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .demo-scrollbar::-webkit-scrollbar-track {
          background: rgba(80, 54, 145, 0.05);
        }

        .demo-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(80, 54, 145, 0.2);
          border-radius: 3px;
        }

        .demo-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(80, 54, 145, 0.3);
        }
      `}</style>

      <div
        className={`fixed inset-0 bg-[rgba(20,29,50,0.7)] backdrop-blur-sm flex items-center justify-center z-[1000] p-5 transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      >
        <div
          className={`demo-popup-card bg-white rounded-[20px] shadow-[0_20px_60px_rgba(80,54,145,0.2)] max-w-[600px] w-full relative max-h-[90vh] overflow-y-auto demo-scrollbar transition-all duration-[400ms] ${
            isAnimating ? 'scale-100 translate-y-0' : 'scale-90 translate-y-[30px]'
          }`}
          style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 w-10 h-10 border-0 bg-[rgba(80,54,145,0.1)] rounded-full cursor-pointer flex items-center justify-center text-white transition-all duration-300 z-10 hover:bg-[rgba(80,54,145,0.2)] hover:rotate-90"
            onClick={handleClose}
          >
            <X size={24} />
          </button>

          {/* Header */}
          <div className="demo-header-pattern pt-4 px-6 pb-2 text-center bg-gradient-to-br from-[#503691] to-[#3f2d7a] text-white rounded-t-[20px] relative overflow-hidden">
            <div className="demo-badge-bounce inline-flex items-center gap-2 bg-[rgba(255,255,255,0.2)] py-1 px-4 rounded-[30px] mb-4">
              <span className="text-lg">🎉</span>
              <span className="text-[12px] font-bold tracking-wider uppercase">Demo Plan Active</span>
            </div>
            <h2 className="text-[18px] font-bold my-3 text-white">Your Demo Account</h2>
            <p className="text-sm text-[rgba(255,255,255,0.9)] m-0">
              Explore all features for the next <strong className="font-bold text-base">{remainingDays} day{remainingDays !== 1 ? 's' : ''}</strong>
            </p>
          </div>

          {/* Active Days Progress */}
          <div className="p-6 bg-[rgba(80,54,145,0.04)] border-b border-[rgba(80,54,145,0.1)]">
            <div className="flex justify-between items-center mb-3 text-sm font-semibold text-[#141d32]">
              <span>Days Remaining</span>
              <span className="bg-gradient-to-br from-[#503691] to-[#7c5cdb] text-white py-1 px-3 rounded-[20px] text-[13px] font-bold">
                {remainingDays}
              </span>
            </div>
            <div className="w-full h-2 bg-[rgba(80,54,145,0.1)] rounded-[10px] overflow-hidden">
              <div
                className="progress-bar-animated h-full bg-gradient-to-r from-[#503691] to-[#7c5cdb] rounded-[10px] transition-all duration-[600ms]"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          
          {/* Contact Limits Grid */}
          <div className="p-6 border-b border-[rgba(80,54,145,0.1)]">
            <h3 className="section-title-line text-base font-bold text-[#141d32] m-0 mb-4 flex items-center gap-2">
              Daily Contact Limits
            </h3>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
              {ownerLimit.daily > 0 && (
                <div className="limit-fade-1 limit-card-shine flex items-center gap-3 p-3.5 rounded-xl border border-[rgba(255,107,107,0.2)] bg-gradient-to-br from-[rgba(255,107,107,0.08)] to-[rgba(255,107,107,0.03)] transition-all duration-300 cursor-pointer relative overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_6px_12px_rgba(80,54,145,0.1)]">
                  <div className="text-2xl min-w-[30px] text-center">🏠</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-[#503691] font-semibold uppercase m-0 leading-[1.2]">Owner Contacts</p>
                    <p className="text-[13px] font-bold text-[#141d32] mt-1 mb-0">
                      <span className="text-[#503691]">{ownerLimit.daily}</span>
                      <span className="text-[rgba(0,0,0,0.3)] mx-[3px]">/</span>
                      <span className="text-[#888] text-xs font-semibold">{ownerLimit.total}</span>
                    </p>
                  </div>
                </div>
              )}

              {brokerLimit.daily > 0 && (
                <div className="limit-fade-2 limit-card-shine flex items-center gap-3 p-3.5 rounded-xl border border-[rgba(78,205,196,0.2)] bg-gradient-to-br from-[rgba(78,205,196,0.08)] to-[rgba(78,205,196,0.03)] transition-all duration-300 cursor-pointer relative overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_6px_12px_rgba(80,54,145,0.1)]">
                  <div className="text-2xl min-w-[30px] text-center">🏢</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-[#503691] font-semibold uppercase m-0 leading-[1.2]">Broker Contacts</p>
                    <p className="text-[13px] font-bold text-[#141d32] mt-1 mb-0">
                      <span className="text-[#503691]">{brokerLimit.daily}</span>
                      <span className="text-[rgba(0,0,0,0.3)] mx-[3px]">/</span>
                      <span className="text-[#888] text-xs font-semibold">{brokerLimit.total}</span>
                    </p>
                  </div>
                </div>
              )}

              {builderLimit.daily > 0 && (
                <div className="limit-fade-3 limit-card-shine flex items-center gap-3 p-3.5 rounded-xl border border-[rgba(255,217,61,0.2)] bg-gradient-to-br from-[rgba(255,217,61,0.08)] to-[rgba(255,217,61,0.03)] transition-all duration-300 cursor-pointer relative overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_6px_12px_rgba(80,54,145,0.1)]">
                  <div className="text-2xl min-w-[30px] text-center">🏗️</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-[#503691] font-semibold uppercase m-0 leading-[1.2]">Builder Contacts</p>
                    <p className="text-[13px] font-bold text-[#141d32] mt-1 mb-0">
                      <span className="text-[#503691]">{builderLimit.daily}</span>
                      <span className="text-[rgba(0,0,0,0.3)] mx-[3px]">/</span>
                      <span className="text-[#888] text-xs font-semibold">{builderLimit.total}</span>
                    </p>
                  </div>
                </div>
              )}

              {requirementLimit.daily > 0 && (
                <div className="limit-fade-4 limit-card-shine flex items-center gap-3 p-3.5 rounded-xl border border-[rgba(155,89,182,0.2)] bg-gradient-to-br from-[rgba(155,89,182,0.08)] to-[rgba(155,89,182,0.03)] transition-all duration-300 cursor-pointer relative overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_6px_12px_rgba(80,54,145,0.1)]">
                  <div className="text-2xl min-w-[30px] text-center">📋</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-[#503691] font-semibold uppercase m-0 leading-[1.2]">Requirement Contacts</p>
                    <p className="text-[13px] font-bold text-[#141d32] mt-1 mb-0">
                      <span className="text-[#503691]">{requirementLimit.daily}</span>
                      <span className="text-[rgba(0,0,0,0.3)] mx-[3px]">/</span>
                      <span className="text-[#888] text-xs font-semibold">{requirementLimit.total}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="actions-fade p-6 flex gap-3 bg-[rgba(80,54,145,0.04)] border-t border-[rgba(80,54,145,0.1)] max-[600px]:flex-col max-[600px]:p-4">
            <button
              className="flex-1 py-3 px-4 border-2 border-[#503691] rounded-[10px] text-sm font-semibold cursor-pointer transition-all duration-300 text-center whitespace-nowrap bg-white text-[#503691] hover:bg-[rgba(80,54,145,0.05)] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(80,54,145,0.15)] max-[600px]:py-3.5 max-[600px]:px-3 max-[600px]:text-[13px]"
              onClick={handleClose}
            >
              Continue with Demo
            </button>
            <button
              className="flex-1 py-3 px-4 border-0 rounded-[10px] text-sm font-semibold cursor-pointer transition-all duration-300 text-center whitespace-nowrap bg-gradient-to-br from-[#503691] to-[#7c5cdb] text-white hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(80,54,145,0.3)] active:translate-y-0 max-[600px]:py-3.5 max-[600px]:px-3 max-[600px]:text-[13px]"
              onClick={handlePurchasePlans}
            >
              🚀 Purchase Plans
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemoPlanPopup;